/**
 * CSP Health Check Utility
 * This file provides functions to diagnose and address Content Security Policy issues
 */

import { logger } from './logger';

interface CSPViolation {
  blockedURI: string;
  documentURI: string;
  effectiveDirective: string;
  originalPolicy: string;
  sourceFile?: string;
  lineNumber?: number;
  columnNumber?: number;
  sample?: string;
}

/**
 * Initialize CSP Health Check & Diagnostics
 * Detects CSP violations and logs them
 */
export function initCSPHealthCheck(): void {
  if (typeof document === 'undefined') return;

  // Only log detailed CSP information in development mode
  const isProduction = import.meta.env.PROD === true;
  
  // Register listener for CSP violations
  document.addEventListener('securitypolicyviolation', (e: SecurityPolicyViolationEvent) => {
    // In production, only log critical violations (not script-src which are common)
    if (isProduction && e.effectiveDirective === 'script-src') {
      return; // Skip logging common script-src violations in production
    }
    
    const violation: CSPViolation = {
      blockedURI: e.blockedURI,
      documentURI: e.documentURI,
      effectiveDirective: e.effectiveDirective,
      originalPolicy: e.originalPolicy,
      sourceFile: e.sourceFile,
      lineNumber: e.lineNumber,
      columnNumber: e.columnNumber,
      sample: e.sample
    };

    logger.warn('CSP Violation:', violation);
    
    // Attempt to fix common issues (only in development)
    if (!isProduction && e.effectiveDirective === 'script-src') {
      attemptScriptCSPFix(e.blockedURI);
    }
  });

  // Add diagnostic information only in development
  if (!isProduction) {
    logger.debug('CSP Health Check initialized');
    
    // Log current nonce information
    if ((window as any).__nonce) {
      logger.debug('CSP Nonce detected:', (window as any).__nonce.substring(0, 6) + '...');
    } else {
      logger.warn('No CSP nonce found in window.__nonce');
    }
  }
}

/**
 * Check if current page has a CSP meta tag (which is discouraged)
 * Returns true if a CSP meta tag is found
 */
export function hasCSPMetaTag(): boolean {
  if (typeof document === 'undefined') return false;
  
  const metaTags = document.querySelectorAll('meta[http-equiv="Content-Security-Policy"]');
  return metaTags.length > 0;
}

/**
 * Attempt to fix script-src CSP violations
 * @param blockedURI The URI that was blocked
 */
function attemptScriptCSPFix(blockedURI: string): void {
  if (typeof document === 'undefined') return;
  
  // Look for scripts with this URI that don't have nonces
  const scripts = document.querySelectorAll(`script[src="${blockedURI}"]`);
  
  scripts.forEach(script => {
    if (!script.hasAttribute('nonce') && (window as any).__nonce) {
      logger.debug(`Fixing script without nonce: ${blockedURI}`);
      script.setAttribute('nonce', (window as any).__nonce);
    }
  });
  
  // If we couldn't find the script, it might be dynamically added later
  // We'll enhance the MutationObserver in secureDynamicScripts
  secureDynamicScripts();
}

/**
 * Enhance security for dynamically added scripts
 */
function secureDynamicScripts(): void {
  if (typeof document === 'undefined' || typeof MutationObserver === 'undefined') return;
  
  // Check if we already have an observer (don't create duplicate)
  if ((window as any).__cspObserver) return;
  
  const nonce = (window as any).__nonce;
  if (!nonce) {
    logger.warn('Cannot secure dynamic scripts: No nonce available');
    return;
  }
  
  // Create a MutationObserver to watch for script additions
  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeName === 'SCRIPT') {
          const scriptNode = node as HTMLScriptElement;
          
          // Add nonce if missing
          if (!scriptNode.hasAttribute('nonce')) {
            scriptNode.setAttribute('nonce', nonce);
            logger.debug('Added nonce to dynamically created script', 
              scriptNode.src ? `src=${scriptNode.src.substring(0, 30)}...` : '(inline script)');
          }
        }
      });
    });
  });
  
  // Start observing the entire document for changes
  observer.observe(document.documentElement, {
    childList: true,
    subtree: true
  });
  
  // Store reference to prevent duplicate observers
  (window as any).__cspObserver = observer;
  
  logger.debug('Dynamic script security enhanced');
}

/**
 * Calculate hash of a string for CSP
 * @param content The content to hash
 * @returns CSP-compatible hash string
 */
export function calculateCSPHash(content: string): string {
  if (typeof crypto === 'undefined') return '';
  
  // Convert string to ArrayBuffer
  const encoder = new TextEncoder();
  const data = encoder.encode(content);
  
  // Calculate hash using SubtleCrypto
  return crypto.subtle.digest('SHA-256', data)
    .then(hashBuffer => {
      // Convert hash to base64
      const hashArray = Array.from(new Uint8Array(hashBuffer));
      const hashBase64 = btoa(String.fromCharCode.apply(null, hashArray));
      
      return `'sha256-${hashBase64}'`;
    })
    .catch(error => {
      logger.error('Error calculating CSP hash:', error);
      return '';
    });
}
